import React, { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { UserIdentityProvider, useUserIdentity } from "./contexts/UserIdentityContext";
import ErrorBoundary from "./components/ErrorBoundary";
import Login from "./components/Login";
import IdentificationModal from "./components/IdentificationModal";
import PharmacySelector from "./components/PharmacySelector";
import EmailVerification from "./components/EmailVerification";
import AdminSetup from "./components/AdminSetup";
import Layout from "./components/Layout";
import {
  Agenda,
  AuditLog,
  Documents,
  Dashboard,
  Tasks,
  Communications,
  Calibrations,
  ExpiryChecks,
  CompoundingPreparations,
  Complaints,
  Settings,
  UserManagement,
  QaReport,
  Training,
} from "./pages";
import { Toaster } from "react-hot-toast";
import { clearAllCaches, detectCacheIssues } from "./utils/clearCache";
import { testDatabaseConnection } from "./utils/testDatabase";
import "./App.css";

// Import connection test utility for development
if (process.env.NODE_ENV === "development") {
  import("./utils/testSupabase").then(({ runConnectionTests }) => {
    // Run Supabase connection tests in development
    setTimeout(runConnectionTests, 1000);
  });

  // Run database table tests
  setTimeout(async () => {
    console.log("Running database table tests...");
    await testDatabaseConnection();
  }, 2000);
}

// Add cache clear button if cache issues detected
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    if (detectCacheIssues()) {
      console.warn("Cache issues detected!");
      // Create emergency cache clear button
      const button = document.createElement("button");
      button.innerHTML = "🧹 Clear Cache & Fix";
      button.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #dc2626;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: bold;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
        transition: all 0.2s;
      `;
      button.onmouseover = () => {
        button.style.transform = "scale(1.05)";
      };
      button.onmouseout = () => {
        button.style.transform = "scale(1)";
      };
      button.onclick = async () => {
        if (
          confirm(
            "This will clear all cached data and reload the page. This should fix the connection issues. Continue?",
          )
        ) {
          try {
            await clearAllCaches();
            window.location.reload();
          } catch (error) {
            console.error("Failed to clear cache:", error);
            alert("Failed to clear cache. Check console for details.");
          }
        }
      };
      document.body.appendChild(button);
    }
  });
}

const AppContent: React.FC = () => {
  const { user, selectedPharmacy, loading, needsEmailVerification } = useAuth();
  const { isIdentified } = useUserIdentity();
  const [currentPage, setCurrentPage] = useState("dashboard");

  // Handle email verification on main page
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("access_token") || hash.includes("type=signup")) {
      // Clear the hash and reload to clean URL
      window.history.replaceState(null, "", window.location.pathname);
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show email verification if user needs to verify email
  if (needsEmailVerification && user) {
    return <EmailVerification />;
  }

  // Show login if no user
  if (!user) {
    return <Login />;
  }

  // Show admin setup for joeri@met.care if not admin yet
  if (user && user.email === "joeri@met.care" && user.role !== "admin") {
    return <AdminSetup />;
  }

  // Show pharmacy selector if user but no selected pharmacy
  if (!selectedPharmacy) {
    return <PharmacySelector />;
  }

  // Render the appropriate page based on currentPage
  const renderPage = () => {
    switch (currentPage) {
      case "agenda":
        return <Agenda onPageChange={setCurrentPage} />;
      case "dashboard":
        return <Dashboard onPageChange={setCurrentPage} />;
      case "tasks":
        return <Tasks />;
      case "communications":
        return <Communications />;
      case "calibrations":
        return <Calibrations />;
      case "expiry-checks":
        return <ExpiryChecks />;
      case "compounding-preparations":
        return <CompoundingPreparations />;
      case "complaints":
        return <Complaints />;
      case "user-management":
        return <UserManagement />;
      case "audit-log":
        return <AuditLog />;
      case "documents":
        return <Documents />;
      case "qa-report":
        return <QaReport />;
      case "training":
        return <Training />;
      case "settings":
        return <Settings />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <>
      <Layout currentPage={currentPage} onPageChange={setCurrentPage}>
        {renderPage()}
      </Layout>
      {!isIdentified && <IdentificationModal />}
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <UserIdentityProvider>
          <div className="App">
            <AppContent />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
                success: {
                  duration: 3000,
                  style: {
                    background: "#10B981",
                  },
                },
                error: {
                  duration: 5000,
                  style: {
                    background: "#EF4444",
                  },
                },
              }}
            />
          </div>
          </UserIdentityProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
